import { useEffect, useState } from 'react'
import { Grid, Typography } from '@material-ui/core'
import { FormattedMessage } from 'react-intl'
import { useFormikContext } from 'formik'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
  SemiConfigFormValues,
  specificationItem,
  PhysicalMaterialTypes,
} from '../ProductsConfiguration'
import StepTitle from '../StepTitle'
import CircleLoader from '@src/components/CircleLoader'
import useSpecifications from '../useSpecifications'
import PhysicalMaterial from './PhysicalMaterial'
import RNASpecifications from './RNASpecifications'
import VirusSpecifications from './VirusSpecifications'
import DNASpecifications from './DNASpecifications'
import PageError from '../PageError'

const useStyles = makeStyles((theme: Theme) => ({
  requiredText: {
    paddingTop: theme.spacing(1),
    '& p': {
      fontSize: theme.typography.pxToRem(12),
    },
  },
  sequenceWithAsterisk: {
    paddingBottom: `${theme.typography.pxToRem(8)}!important`,
  },
}))

const Specifications = () => {
  const formik = useFormikContext<SemiConfigFormValues>()
  const classes = useStyles()
  const { SpecificationsQuery, checkForValidSpecification } =
    useSpecifications()
  const [physicalMaterial, setReqPhysicalMaterial] = useState<
    specificationItem[]
  >([])
  const [species, setSpecies] = useState<specificationItem[]>([])
  const [structure, setStructure] = useState<specificationItem[]>([])
  const [scale, setScale] = useState<specificationItem[]>([])
  const [modification, setModification] = useState<specificationItem[]>([])
  const [purification, setPurification] = useState<specificationItem[]>([])
  const [format, setFormat] = useState<specificationItem[]>([])
  const [vector, setVector] = useState<specificationItem[]>([])
  const [amount, setAmount] = useState<specificationItem[]>([])
  const [lentivirusBackbone, setLentivirusBackbone] = useState<
    specificationItem[]
  >([])
  const [titer, setTiter] = useState<specificationItem[]>([])
  const [volume, setVolume] = useState<specificationItem[]>([])
  const [aliquot, setAaliquot] = useState<specificationItem[]>([])

  const onSpecificationChange = (field: string, value: string) => {
    formik.setFieldValue(`specifications.[${field}]`, value, false)
    if (formik.values.completedStep === 3)
      formik.setFieldValue('completedStep', 2, false)
    //clear specifications related to other physical materials
    if (
      formik.values.specifications.physicalMaterial ===
      PhysicalMaterialTypes.RNA
    ) {
      if (formik.values.specifications.vector)
        formik.setFieldValue('specifications.vector', '', false)
      if (formik.values.specifications.amount)
        formik.setFieldValue('specifications.amount', '', false)
      if (formik.values.specifications.lentivirusBackbone)
        formik.setFieldValue('specifications.lentivirusBackbone', '', false)
      if (formik.values.specifications.titer)
        formik.setFieldValue('specifications.titer', '', false)
      if (formik.values.specifications.volume)
        formik.setFieldValue('specifications.volume', '', false)
      if (formik.values.specifications.aliquot)
        formik.setFieldValue('specifications.aliquot', '', false)
    } else if (
      formik.values.specifications.physicalMaterial ===
      PhysicalMaterialTypes.DNA
    ) {
      if (formik.values.specifications.species)
        formik.setFieldValue('specifications.species', '', false)
      if (formik.values.specifications.structure)
        formik.setFieldValue('specifications.structure', '', false)
      if (formik.values.specifications.scale)
        formik.setFieldValue('specifications.scale', '', false)
      if (formik.values.specifications.modification)
        formik.setFieldValue('specifications.modification', '', false)
      if (formik.values.specifications.purification)
        formik.setFieldValue('specifications.purification', '', false)
      if (formik.values.specifications.format)
        formik.setFieldValue('specifications.format', '', false)
      if (formik.values.specifications.lentivirusBackbone)
        formik.setFieldValue('specifications.lentivirusBackbone', '', false)
      if (formik.values.specifications.titer)
        formik.setFieldValue('specifications.titer', '', false)
      if (formik.values.specifications.volume)
        formik.setFieldValue('specifications.volume', '', false)
      if (formik.values.specifications.aliquot)
        formik.setFieldValue('specifications.aliquot', '', false)
    } else if (
      formik.values.specifications.physicalMaterial ===
      PhysicalMaterialTypes.VIRUS
    ) {
      if (formik.values.specifications.vector)
        formik.setFieldValue('specifications.vector', '', false)
      if (formik.values.specifications.amount)
        formik.setFieldValue('specifications.amount', '', false)
      if (formik.values.specifications.species)
        formik.setFieldValue('specifications.species', '', false)
      if (formik.values.specifications.structure)
        formik.setFieldValue('specifications.structure', '', false)
      if (formik.values.specifications.scale)
        formik.setFieldValue('specifications.scale', '', false)
      if (formik.values.specifications.modification)
        formik.setFieldValue('specifications.modification', '', false)
      if (formik.values.specifications.purification)
        formik.setFieldValue('specifications.purification', '', false)
      if (formik.values.specifications.format)
        formik.setFieldValue('specifications.format', '', false)
    }
    //if field is physical material, reset validation messages for other fields
    //display only for physical material if it is empty.
    if (
      field === 'physicalMaterial' &&
      value !== '' &&
      formik.values.displayPageError
    )
      formik.setFieldValue('displayPageError', false, false)
  }
  const checkForValueInList = (
    name: string,
    list: specificationItem[],
    onlyCheckForExistanceInList?: boolean
  ) => {
    if (
      onlyCheckForExistanceInList &&
      list.length === 1 &&
      formik.values.specifications?.[name] !== list[0].key
    ) {
      formik.setFieldValue(`specifications.[${name}]`, list[0].key, false)
    } else {
      const filteredList = list.filter((item) => item.enabled)
      if (
        filteredList.length === 1 &&
        formik.values.specifications?.[name] !== filteredList[0].key
      )
        formik.setFieldValue(
          `specifications.[${name}]`,
          filteredList[0].key,
          false
        )
      else if (
        formik.values.specifications?.[name] &&
        !list.find(
          (item) =>
            item.key === formik.values.specifications?.[name] &&
            (onlyCheckForExistanceInList || item.enabled)
        )
      )
        formik.setFieldValue(`specifications.[${name}]`, '', false)
    }
  }
  useEffect(() => {
    const loadData = async () => {
      const data = await SpecificationsQuery({})
      if (data.physicalMaterial) setReqPhysicalMaterial(data.physicalMaterial)

      if (data.species) {
        setSpecies(data.species)
        checkForValueInList('species', data.species)
      }

      if (data.structure) {
        setStructure(data.structure)
        checkForValueInList('structure', data.structure)
      }
      if (data.scale) {
        setScale(data.scale)
        checkForValueInList('scale', data.scale)
      }
      if (data.modification) {
        setModification(data.modification)
        checkForValueInList('modification', data.modification)
      }
      if (data.purification) {
        setPurification(data.purification)
        checkForValueInList('purification', data.purification)
      }
      if (data.format) {
        setFormat(data.format)
        checkForValueInList('format', data.format)
      }
      if (data.vector) {
        setVector(data.vector)
        // checkForValueInList('vector', data.vector)
      }
      if (data.amount) {
        setAmount(data.amount)
        checkForValueInList('amount', data.amount, true)
      }
      if (data.lentivirusBackbone) {
        setLentivirusBackbone(data.lentivirusBackbone)
      }
      if (data.titer) {
        setTiter(data.titer)
      }
      if (data.volume) {
        setVolume(data.volume)
        checkForValueInList('volume', data.volume, true)
      }
      if (data.aliquot) {
        setAaliquot(data.aliquot)
        checkForValueInList('aliquot', data.aliquot, true)
      }
    }
    loadData()
  }, [formik.values.specifications])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <Grid container>
      <StepTitle
        title={
          <FormattedMessage
            id="SPECIFICATIONS"
            defaultMessage="Specifications"
          />
        }
      />
      <Grid item xs={12} className={classes.requiredText}>
        <Typography variant="body1">
          *
          <FormattedMessage
            id="REQUIRED_FIELDS"
            defaultMessage="Required Fields"
          />
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <PageError
          showError={
            formik.values.displayPageError &&
            formik.values.completedStep >= 2 &&
            !checkForValidSpecification()
          }
          errorMessage={
            <FormattedMessage
              id="CONF_SELECT_REQUIRED_FIELD_crispgrna"
              defaultMessage="Please select the Required Fields"
            />
          }
          rootStyle={{ marginTop: '24px' }}
        />
      </Grid>
      <PhysicalMaterial
        physicalMaterialList={physicalMaterial}
        onChange={onSpecificationChange}
      />
      {formik.values.specifications.physicalMaterial ===
        PhysicalMaterialTypes.RNA && (
        <RNASpecifications
          speciesList={species}
          structureList={structure}
          scaleList={scale}
          modificationList={modification}
          purificationList={purification}
          formatList={format}
          onChange={onSpecificationChange}
        />
      )}
      {formik.values.specifications.physicalMaterial ===
        PhysicalMaterialTypes.DNA && (
        <DNASpecifications
          vectorList={vector}
          amountList={amount}
          onChange={onSpecificationChange}
        />
      )}
      {formik.values.specifications.physicalMaterial ===
        PhysicalMaterialTypes.VIRUS && (
        <VirusSpecifications
          lentivirusBackboneList={lentivirusBackbone}
          titerList={titer}
          volumeList={volume}
          aliquotList={aliquot}
          onChange={onSpecificationChange}
        />
      )}
      {formik.values?.loading && <CircleLoader />}
    </Grid>
  )
}

export default Specifications
